import api from '../utils/api';

/**
 * Commission Service - Handles vendor commission and payout management
 */

export const commissionService = {
  // Commission Rates
  /**
   * Get commission rates (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getCommissionRates: async (filters = {}) => {
    try {
      const response = await api.get('/admin/commissions/rates', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commission rates');
    }
  },

  /**
   * Create commission rate (admin only)
   * @param {Object} rateData - Rate data
   * @returns {Promise}
   */
  createCommissionRate: async (rateData) => {
    try {
      const response = await api.post('/admin/commissions/rates', rateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create commission rate');
    }
  },

  /**
   * Update commission rate (admin only)
   * @param {string} id - Rate ID
   * @param {Object} rateData - Updated rate data
   * @returns {Promise}
   */
  updateCommissionRate: async (id, rateData) => {
    try {
      const response = await api.put(`/admin/commissions/rates/${id}`, rateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update commission rate');
    }
  },

  /**
   * Delete commission rate (admin only)
   * @param {string} id - Rate ID
   * @returns {Promise}
   */
  deleteCommissionRate: async (id) => {
    try {
      const response = await api.delete(`/admin/commissions/rates/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete commission rate');
    }
  },

  // Commissions (Admin)
  /**
   * Get all commissions (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAllCommissions: async (filters = {}) => {
    try {
      const response = await api.get('/admin/commissions', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commissions');
    }
  },

  /**
   * Get commission by ID (admin only)
   * @param {string} id - Commission ID
   * @returns {Promise}
   */
  getCommissionById: async (id) => {
    try {
      const response = await api.get(`/admin/commissions/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commission');
    }
  },

  /**
   * Get commission statistics (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getCommissionStats: async (filters = {}) => {
    try {
      const response = await api.get('/admin/commissions/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commission stats');
    }
  },

  /**
   * Update commission status (admin only)
   * @param {string} id - Commission ID
   * @param {Object} updateData - Status and notes
   * @returns {Promise}
   */
  updateCommissionStatus: async (id, updateData) => {
    try {
      const response = await api.put(`/admin/commissions/${id}/status`, updateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update commission status');
    }
  },

  /**
   * Calculate commissions (admin only)
   * @param {Object} calculateData - Order ID, vendor ID, date range
   * @returns {Promise}
   */
  calculateCommissions: async (calculateData) => {
    try {
      const response = await api.post('/admin/commissions/calculate', calculateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to calculate commissions');
    }
  },

  // Vendor Commissions
  /**
   * Get vendor's commissions
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getVendorCommissions: async (filters = {}) => {
    try {
      const response = await api.get('/vendor/commissions', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commissions');
    }
  },

  /**
   * Get vendor commission statistics
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getVendorCommissionStats: async (filters = {}) => {
    try {
      const response = await api.get('/vendor/commissions/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch commission stats');
    }
  },

  // Payouts (Admin)
  /**
   * Get all payouts (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAllPayouts: async (filters = {}) => {
    try {
      const response = await api.get('/admin/payouts', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payouts');
    }
  },

  /**
   * Get payout statistics (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getPayoutStats: async (filters = {}) => {
    try {
      const response = await api.get('/admin/payouts/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payout stats');
    }
  },

  /**
   * Create payout (admin only)
   * @param {Object} payoutData - Payout data
   * @returns {Promise}
   */
  createPayout: async (payoutData) => {
    try {
      const response = await api.post('/admin/payouts', payoutData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create payout');
    }
  },

  /**
   * Get payout by ID (admin only)
   * @param {string} id - Payout ID
   * @returns {Promise}
   */
  getPayoutById: async (id) => {
    try {
      const response = await api.get(`/admin/payouts/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payout');
    }
  },

  /**
   * Update payout status (admin only)
   * @param {string} id - Payout ID
   * @param {Object} updateData - Status, transaction ID, etc.
   * @returns {Promise}
   */
  updatePayoutStatus: async (id, updateData) => {
    try {
      const response = await api.put(`/admin/payouts/${id}/status`, updateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update payout status');
    }
  },

  /**
   * Process payout (admin only)
   * @param {string} id - Payout ID
   * @param {string} transactionId - Transaction ID
   * @returns {Promise}
   */
  processPayout: async (id, transactionId) => {
    try {
      const response = await api.post(`/admin/payouts/${id}/process`, { transactionId });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to process payout');
    }
  },

  /**
   * Export payouts to CSV (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  exportPayouts: async (filters = {}) => {
    try {
      const response = await api.post('/admin/payouts/export', filters, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export payouts');
    }
  },

  // Vendor Payouts
  /**
   * Get vendor's payouts
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getVendorPayouts: async (filters = {}) => {
    try {
      const response = await api.get('/vendor/payouts', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payouts');
    }
  },

  /**
   * Get vendor payout by ID
   * @param {string} id - Payout ID
   * @returns {Promise}
   */
  getVendorPayoutById: async (id) => {
    try {
      const response = await api.get(`/vendor/payouts/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payout');
    }
  }
};

